import type {
  PromotionBox,
  PromotionBoxAsset,
  PromotionBoxLine,
  PromotionGesture,
  SyncRefusalReason,
  VersionedKind,
} from '@matter_extractor/shared';
import { promotionCounts } from '@matter_extractor/shared';

/**
 * The words and the arrangement of the box of the cascade (vitrine.md §4.3, issue #192; maquette
 * `passage-en-prod.html`, variant B): pure, so they are tested without a screen.
 *
 * The box shows the verdict of the prod BEFORE the click: a summary line (« 7 éléments partent,
 * 1 refus »), then the cascade in groups folded by kind, each line saying what goes, how it is
 * cited, what the prod holds already and who else it touches. A group that carries a refusal
 * opens by itself and names the reason.
 */

/** A row of a group: a version of the cascade, or a media file it cites. */
export type CascadeRow =
  | { readonly type: 'version'; readonly line: PromotionBoxLine }
  | { readonly type: 'asset'; readonly asset: PromotionBoxAsset };

export type CascadeGroupId = VersionedKind | 'assets';

export interface CascadeGroup {
  readonly id: CascadeGroupId;
  readonly title: string;
  /** The sub-title the maquette puts beside the title: « un par page de protocole ». */
  readonly subtitle: string | null;
  readonly rows: readonly CascadeRow[];
  readonly sends: number;
  readonly already: number;
  readonly refused: number;
}

/** In the order the maquette lists them: what the operator clicked first, the leaves last. */
const GROUPS: readonly { id: CascadeGroupId; title: string; subtitle: string | null }[] = [
  { id: 'experience', title: 'Expérience', subtitle: null },
  { id: 'protocol', title: 'Protocoles', subtitle: 'un par page de protocole' },
  { id: 'persona', title: 'Persona', subtitle: null },
  { id: 'criterion', title: 'Critères', subtitle: 'par les protocoles' },
  { id: 'noyau-texts', title: 'Textes des noyaux', subtitle: null },
  { id: 'models', title: 'Modèles', subtitle: null },
  { id: 'rules', title: 'Règles', subtitle: null },
  { id: 'method', title: 'Méthode', subtitle: null },
  { id: 'assets', title: 'Médias', subtitle: 'médiathèque' },
];

/** The cascade of a box, grouped by kind; an empty group is not drawn. */
export function cascadeGroups(box: Pick<PromotionBox, 'lines' | 'assets'>): CascadeGroup[] {
  return GROUPS.flatMap((group) => {
    const rows: CascadeRow[] =
      group.id === 'assets'
        ? box.assets.map((asset) => ({ type: 'asset', asset }))
        : box.lines
            .filter((line) => line.kind === group.id)
            .map((line) => ({ type: 'version', line }));
    if (rows.length === 0) return [];
    const statuses = rows.map((row) =>
      row.type === 'version' ? row.line.status : row.asset.status,
    );
    return [
      {
        ...group,
        rows,
        sends: statuses.filter((status) => status === 'sends').length,
        already: statuses.filter((status) => status === 'already').length,
        refused: statuses.filter((status) => status === 'refused').length,
      },
    ];
  });
}

/** « 1 partent » is not French: the verb agrees with the count. */
function count(n: number, one: string, many: string): string {
  return `${n} ${n > 1 ? many : one}`;
}

/** What a group says beside its title: « 2 partent, 1 déjà en prod ». */
export function groupSummary(group: CascadeGroup): string {
  const parts: string[] = [];
  if (group.sends > 0) parts.push(count(group.sends, 'part', 'partent'));
  if (group.already > 0) parts.push(`${group.already} déjà en prod`);
  return parts.join(', ');
}

/** The sentence of the summary line, once the prod has answered (§4.3). */
export function summaryOf(box: Pick<PromotionBox, 'lines' | 'assets' | 'gestureRefusal'>): string {
  const { sends, already, refused } = promotionCounts(box);
  if (box.gestureRefusal !== undefined) return 'La prod refuse ce geste : rien ne change.';
  if (refused > 0) {
    return `${count(sends + refused, 'élément à envoyer', 'éléments à envoyer')}, ${count(refused, 'refus', 'refus')} : rien ne part`;
  }
  const head = sends > 0 ? count(sends, 'élément part', 'éléments partent') : 'Rien à envoyer';
  return `${head}${already > 0 ? `, ${already} déjà en prod` : ''}, aucun refus`;
}

/** The label of the confirm button: it says how many elements go, and it is inert unless all pass. */
export function confirmLabelOf(
  gesture: PromotionGesture,
  box: Pick<PromotionBox, 'lines' | 'assets'> | null,
): string {
  if (gesture.type === 'pointer') return 'Reculer latest prod';
  const sends = box === null ? 0 : promotionCounts(box).sends;
  return sends > 0
    ? `Passer en production (${count(sends, 'élément', 'éléments')})`
    : 'Passer en production';
}

/** Why the confirm button is what it is, under it (maquette `.why`). */
export function whyOf(state: PromotionBox['state'] | 'checking'): string {
  switch (state) {
    case 'checking':
      return 'Le bouton attend le verdict de la prod.';
    case 'ok':
      return 'La prod applique la cascade d’un bloc ; le dev ne bouge qu’après.';
    case 'refused':
      return 'Une ligne est refusée : la cascade part tout entière ou pas du tout.';
    case 'unreachable':
      return 'Pas de passage sans le verdict de la prod.';
    case 'local':
      return 'Aucune prod reliée : le passage reste local.';
  }
}

/** How a version of the cascade is cited: the gesture itself, `latest`, or a pinned version. */
export function citationsOf(
  line: PromotionBoxLine,
): readonly { kind: 'geste' | 'lat' | 'pin'; text: string }[] {
  if (line.root) return [{ kind: 'geste', text: 'le geste' }];
  return line.pins.map((pin) =>
    pin === 'latest'
      ? { kind: 'lat' as const, text: 'latest' }
      : { kind: 'pin' as const, text: `épinglé v${pin}` },
  );
}

/** What goes, and what « latest prod » does (maquette `what`). */
export function whatOf(line: PromotionBoxLine): string {
  if (line.status === 'already' && !line.advancesPointer) return 'déjà en prod : rien à envoyer';
  const head =
    line.status === 'already'
      ? `déjà en prod en v${line.versionNumber}`
      : `part en v${line.versionNumber}`;
  if (!line.advancesPointer) {
    return line.prodNumber === null
      ? head
      : `${head} · latest prod reste sur la v${line.prodNumber}`;
  }
  return line.prodNumber === null
    ? `${head} · première version en prod`
    : `${head} · latest prod v${line.prodNumber} → v${line.versionNumber}`;
}

/** « Le Mur v4, Murex v2 » - the referents the prod plays by latest and this move changes. */
export function alsoViaLatestOf(line: PromotionBoxLine): string | null {
  if (line.alsoViaLatest.length === 0) return null;
  return line.alsoViaLatest.map((cited) => `${cited.name} v${cited.number}`).join(', ');
}

/** The reason of a refusal, in a sentence: the code is for the box, the message is the prod's. */
export function reasonOf(reason: SyncRefusalReason | undefined): string {
  return reason?.message ?? 'La prod refuse cette ligne.';
}
